import type { NormalizedIoContainer } from "@/components/Spotflow/WorkflowRunner/Outputs/ioFileDisplay";

const GATE_KEYS = new Set(["when_true", "when_false"]);

// The Conditional's `when_true`/`when_false` outputs are routing gates: the
// branch that fired emits `true`, the other `Skipped()`. Shown raw
// ("when_true: true" / "when_false: true") they read like data; collapse them
// into a single "Conditional: true|false" row where the first gate was. A
// fan-out stores each item's gates, so there the row holds one answer per
// iteration, and an item that took neither keeps its skip marker.
export function conditionGateRows(
  entries: Array<[string, NormalizedIoContainer]>
): Array<[string, NormalizedIoContainer]> {
  const firstGate = entries.findIndex(([key]) => GATE_KEYS.has(key));
  if (firstGate === -1) return entries;
  const whenTrue = entries.find(([key]) => key === "when_true")?.[1];
  const whenFalse = entries.find(([key]) => key === "when_false")?.[1];
  const rest = entries.filter(([key]) => !GATE_KEYS.has(key));

  let row: NormalizedIoContainer | undefined;
  if (Array.isArray(whenTrue?.value) || Array.isArray(whenFalse?.value)) {
    const trues = Array.isArray(whenTrue?.value) ? whenTrue.value : [];
    const falses = Array.isArray(whenFalse?.value) ? whenFalse.value : [];
    const value = Array.from(
      { length: Math.max(trues.length, falses.length) },
      (_, i) =>
        trues[i] === true
          ? true
          : falses[i] === true
            ? false
            : (trues[i] ?? falses[i])
    );
    const source = Array.isArray(whenTrue?.value) ? whenTrue : whenFalse;
    row = { ...source, type: "bool", value };
  } else if (whenTrue?.value === true) {
    row = { type: "bool", value: true };
  } else if (whenFalse?.value === true) {
    row = { type: "bool", value: false };
  }
  if (!row) return rest;
  return [
    ...rest.slice(0, firstGate),
    ["Conditional", row],
    ...rest.slice(firstGate),
  ];
}
